import { useIntl } from 'react-intl';
import { StyleSheet, Text, View } from 'react-native';

import { primitive } from 'styles/constants/primitives';

interface GreetingSectionProps {
  userName: string;
  currentPoints: number;
  displayedPoints: number;
  reachedLastTier: boolean;
}

export const GreetingSection = ({
  userName,
  currentPoints,
  displayedPoints,
  reachedLastTier,
}: GreetingSectionProps) => {
  const { formatMessage } = useIntl();

  const getGreetingMessage = () => {
    if (currentPoints === 0) {
      return formatMessage(
        { id: 'letsGetStarted', defaultMessage: `Let's get started, {userName}!` },
        { userName }
      );
    }

    if (reachedLastTier) {
      return formatMessage(
        {
          id: 'topLevelUnlocked',
          defaultMessage: `Congratulations, {userName}! Top level unlocked!`,
        },
        { userName }
      );
    }

    return formatMessage(
      { id: 'niceWork', defaultMessage: `Nice work, {userName}! You've earned` },
      { userName }
    );
  };

  const getPointsLabel = () => {
    return currentPoints > 0
      ? formatMessage({ id: 'availableCrowns', defaultMessage: 'Available Crowns' })
      : formatMessage({ id: 'crowns', defaultMessage: 'Crowns' });
  };

  const getAccessibilityLabel = () => {
    const greeting = getGreetingMessage();
    const pointsLabel = getPointsLabel();
    return `${greeting} ${displayedPoints} ${pointsLabel}`;
  };

  return (
    <View
      style={styles.greetingSection}
      accessible={true}
      accessibilityRole="text"
      accessibilityLabel={getAccessibilityLabel()}
      testID="greeting-section"
    >
      <Text style={styles.greetingText} accessibilityRole="header" accessibilityLevel={2}>
        {getGreetingMessage()}
      </Text>
      <Text
        style={styles.points}
        accessibilityRole="text"
        accessibilityLabel={`${displayedPoints} points`}
        accessibilityLiveRegion="polite"
      >
        {displayedPoints}
      </Text>
      <Text
        style={styles.pointsLabel}
        accessibilityRole="text"
        accessibilityLabel={getPointsLabel()}
      >
        {getPointsLabel()}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  greetingSection: { zIndex: 2, alignItems: 'center', marginBottom: 20 },
  greetingText: {
    lineHeight: 36,
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: primitive.fontSizes.md,
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.bk.$bbqBrown,
  },
  points: {
    lineHeight: 36,
    fontSize: primitive.fontSizes['4xl'],
    fontFamily: primitive.fontFamily.$bold,
    color: primitive.bk.$bbqBrown,
  },
  pointsLabel: {
    lineHeight: 21,
    fontSize: primitive.fontSizes.sm,
    fontFamily: primitive.fontFamily.$body,
    color: primitive.bk.$bbqBrown,
  },
});
